import React from "react";
import { Badge, Text } from "@mantine/core";
import { IconAlertCircle, IconCircleCheck, IconEye } from "@tabler/icons-react";

export const verificationStatusColor = (status) => {
  if (status === "verified") return "green";
  if (status === "needs_review") return "yellow";
  return "red";
};

// "document_date_check" -> "Document Date Check"
const humanizeCriterionName = (name) =>
  (name || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// The app-wide `body { text-transform: capitalize }` reads fine on short
// labels but makes AI-generated prose (full sentences) hard to read, so
// paragraph text in this component opts back out of it.
const proseStyle = { textTransform: "none" };

const scoreColor = (score) => {
  if (score === null || score === undefined) return "#868e96";
  if (score >= 75) return "#2f9e44";
  if (score >= 40) return "#f08c00";
  return "#e03131";
};

const BANNER_BY_STATUS = {
  verified: { label: "ACCEPT", bg: "#ebfbee", color: "#2f9e44", Icon: IconCircleCheck },
  needs_review: { label: "NEEDS REVIEW", bg: "#fff9db", color: "#e8590c", Icon: IconEye },
  rejected: { label: "REJECT", bg: "#fff5f5", color: "#e03131", Icon: IconAlertCircle },
};

const ScoreBar = ({ label, value }) => {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="flex flex-col items-center" style={{ width: 110 }}>
      <div className="flex items-end justify-center" style={{ height: 140 }}>
        <div
          style={{
            width: 64,
            height: `${pct}%`,
            backgroundColor: scoreColor(value),
            borderRadius: "4px 4px 0 0",
            transition: "height 0.3s ease",
          }}
        />
      </div>
      <Text size="sm" className="mt-2" style={proseStyle} color="dimmed">
        {label}
      </Text>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div
    className="flex-1 p-4 rounded-lg bg-white text-center"
    style={{ border: "1px solid #e5e9eb", minWidth: 130 }}
  >
    <Text size="sm" color="dimmed" style={proseStyle}>
      {label}
    </Text>
    <Text size="28px" weight={700} className="mt-1" style={{ color, ...proseStyle }}>
      {value}
    </Text>
  </div>
);

// Renders a single AI evidence-verification result: relevance/quality score
// chart, duplicate flag, an accept/review/reject banner, the AI's reasoning,
// extracted document text, and any failed/not-found checks with their
// explanations. Shared between the live "Verify Evidence" flow
// (ViewObligation.jsx) and the audit history table's past-results view
// (actionHistory.jsx), which both work with the same result shape returned
// by AI-Server's generic verification endpoint.
const EvidenceVerificationResult = ({ result }) => {
  if (!result) return null;

  const failedCriteria = (result.criteria || []).filter((c) =>
    ["failed", "not_found"].includes(c.status)
  );
  const hasScores =
    typeof result.relevance_score === "number" || typeof result.quality_score === "number";
  const banner = BANNER_BY_STATUS[result.status] || BANNER_BY_STATUS.rejected;

  return (
    <div className="p-2">
      <Text size="lg" weight={700} className="mb-4" style={proseStyle}>
        AI Analysis Result
      </Text>

      {hasScores && (
        <div
          className="flex justify-center gap-12 mb-4 pt-3"
          style={{ borderLeft: "1px solid #dee2e6", borderBottom: "1px solid #dee2e6" }}
        >
          <ScoreBar label="Relevance" value={result.relevance_score} />
          <ScoreBar label="Quality" value={result.quality_score} />
        </div>
      )}

      <div className="flex gap-3 mb-4 flex-wrap">
        {typeof result.relevance_score === "number" && (
          <StatCard
            label="Relevance Score"
            value={`${Math.round(result.relevance_score)}%`}
            color={scoreColor(result.relevance_score)}
          />
        )}
        {typeof result.quality_score === "number" && (
          <StatCard
            label="Quality Score"
            value={`${Math.round(result.quality_score)}%`}
            color={scoreColor(result.quality_score)}
          />
        )}
        <StatCard
          label="Duplicate"
          value={result.isDuplicate ? "YES" : "NO"}
          color={result.isDuplicate ? "#e03131" : "#2f9e44"}
        />
      </div>

      <div
        className="flex items-center gap-2 p-3 rounded-lg mb-4"
        style={{ backgroundColor: banner.bg, color: banner.color }}
      >
        <banner.Icon size={18} />
        <Text weight={700} style={proseStyle}>
          {banner.label}
        </Text>
      </div>

      <div
        className="p-3 rounded-lg mb-4"
        style={{ backgroundColor: "#f8f9fa", borderLeft: "4px solid #f08c00" }}
      >
        <Text weight={600} className="mb-1" style={proseStyle}>
          AI Reasoning
        </Text>
        <Text size="sm" style={proseStyle}>
          {result.summary}
        </Text>
      </div>

      {result.extracted_text && (
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            <Text weight={600} style={proseStyle}>
              Extracted Document Text
            </Text>
            <Badge size="xs" variant="light" color="gray">
              {result.extracted_text.length} characters
            </Badge>
          </div>
          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: "#f1f3f5",
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
              maxHeight: 180,
              overflowY: "auto",
            }}
          >
            {result.extracted_text}
          </div>
        </div>
      )}

      {failedCriteria.length > 0 && (
        <div className="mt-4">
          <Text weight={600} className="mb-2" style={proseStyle}>
            Failed Checks
          </Text>
          {failedCriteria.map((criterion, index) => (
            <div key={index} className="mt-2 p-2" style={{ borderLeft: "3px solid #e03131" }}>
              <Text className="font-semibold text-sm" style={proseStyle}>
                {humanizeCriterionName(criterion.name)}
              </Text>
              <Text className="text-sm" style={proseStyle}>
                {criterion.explanation}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EvidenceVerificationResult;
